import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';

export interface BadgeProps {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  size?: 'small' | 'medium' | 'large';
  dot?: boolean;
  className?: string;
}

const getVariantStyles = (variant: BadgeProps['variant']): any => {
  const variants = {
    primary: css`
      background-color: ${({ theme }) => theme.colors.primary.main};
      color: ${({ theme }) => theme.colors.primary.contrastText};
    `,
    secondary: css`
      background-color: ${({ theme }) => theme.colors.neutral[700]};
      color: ${({ theme }) => theme.colors.text.primary};
    `,
    success: css`
      background-color: ${({ theme }) => theme.colors.success.main};
      color: ${({ theme }) => theme.colors.success.contrastText};
    `,
    error: css`
      background-color: ${({ theme }) => theme.colors.error.main};
      color: ${({ theme }) => theme.colors.error.contrastText};
    `,
    warning: css`
      background-color: ${({ theme }) => theme.colors.warning.main};
      color: ${({ theme }) => theme.colors.warning.contrastText};
    `,
    info: css`
      background-color: ${({ theme }) => theme.colors.info.main};
      color: ${({ theme }) => theme.colors.info.contrastText};
    `,
  };

  return variants[variant || 'primary'];
};

const getSizeStyles = (size: BadgeProps['size'], dot?: boolean): any => {
  if (dot) {
    const dotSizes = {
      small: css`
        width: 6px;
        height: 6px;
      `,
      medium: css`
        width: 8px;
        height: 8px;
      `,
      large: css`
        width: 10px;
        height: 10px;
      `,
    };
    return dotSizes[size || 'medium'];
  }

  const sizes = {
    small: css`
      padding: ${({ theme }) => theme.spacing[0.5]}
        ${({ theme }) => theme.spacing[1.5]};
      font-size: ${({ theme }) => theme.typography.fontSize.xs};
    `,
    medium: css`
      padding: ${({ theme }) => theme.spacing[1]}
        ${({ theme }) => theme.spacing[2]};
      font-size: ${({ theme }) => theme.typography.fontSize.sm};
    `,
    large: css`
      padding: ${({ theme }) => theme.spacing[1.5]}
        ${({ theme }) => theme.spacing[3]};
      font-size: ${({ theme }) => theme.typography.fontSize.base};
    `,
  };

  return sizes[size || 'medium'];
};

interface StyledBadgeProps {
  $variant?: BadgeProps['variant'];
  $size?: BadgeProps['size'];
  $dot?: boolean;
}

const StyledBadge = styled.span<StyledBadgeProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  line-height: 1;
  border-radius: ${({ $dot }) =>
    $dot ? '50%' : ({ theme }) => theme.borderRadius.full};
  white-space: nowrap;

  ${({ $variant }) => getVariantStyles($variant)}
  ${({ $size, $dot }) => getSizeStyles($size, $dot)}
`;

export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'primary',
  size = 'medium',
  dot = false,
  className,
}) => {
  return (
    <StyledBadge
      $variant={variant}
      $size={size}
      $dot={dot}
      className={className}
    >
      {!dot && children}
    </StyledBadge>
  );
};

export default Badge;
